import styled, { css } from 'styled-components';

import { IconButtonSize, IconButtonVariant, ValueOf } from '../../types';
import { subtractUnit } from '../../utils/styles';
import ButtonPrimitive from '../Button/ButtonPrimitive';

import { IconButtonVariableNames } from './constants';

interface ButtonProps {
  $variant: IconButtonVariant;
  $size: IconButtonSize;
}

const variableStyles = css<ButtonProps>(
  ({
    theme,
    $size,
    $variant,
  }): Record<ValueOf<typeof IconButtonVariableNames>, string | undefined> => {
    const variant = theme.components.IconButton[$variant];

    const borderWidth = variant['border-width'];

    const padding = subtractUnit(variant.padding[$size], borderWidth);

    return {
      [IconButtonVariableNames.Color]: variant['icon-default'] || 'initial',
      [IconButtonVariableNames.BackgroundColor]:
        variant['bg-default'] || 'initial',
      [IconButtonVariableNames.BorderColor]:
        variant['border-default'] || 'transparent',
      [IconButtonVariableNames.BorderWidth]: borderWidth,
      [IconButtonVariableNames.HoverBackgroundColor]: variant['bg-hover'],
      [IconButtonVariableNames.HoverColor]: variant['icon-hover'],
      [IconButtonVariableNames.HoverBorderColor]: variant['border-hover'],
      [IconButtonVariableNames.FocusOutlineColor]:
        variant.shadow || 'transparent',
      [IconButtonVariableNames.ExpandedBackgroundColor]: variant['bg-focus'],
      [IconButtonVariableNames.ExpandedOutlineColor]: variant.shadow,
      [IconButtonVariableNames.DisabledColor]: variant['icon-disabled'],
      [IconButtonVariableNames.DisabledBackgroundColor]: variant['bg-disabled'],
      [IconButtonVariableNames.Padding]: padding,
      [IconButtonVariableNames.IconSize]: variant['icon-size'][$size],
    };
  },
);

export const ButtonElement = styled(ButtonPrimitive)<ButtonProps>`
  ${variableStyles}

  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.core.radius.full};
  transition-duration: 0.2s;
  flex: 0;
  width: min-content;
  height: min-content;

  padding: var(${IconButtonVariableNames.Padding});
  color: var(${IconButtonVariableNames.Color});
  background-color: var(${IconButtonVariableNames.BackgroundColor});
  border: var(${IconButtonVariableNames.BorderWidth}) solid
    var(${IconButtonVariableNames.BorderColor});

  svg {
    width: var(${IconButtonVariableNames.IconSize});
    height: var(${IconButtonVariableNames.IconSize});
    transition: fill 0.1s linear;
    fill: currentColor;
  }

  &:hover {
    background-color: var(
      ${IconButtonVariableNames.HoverBackgroundColor},
      var(${IconButtonVariableNames.BackgroundColor})
    );
    color: var(
      ${IconButtonVariableNames.HoverColor},
      var(${IconButtonVariableNames.Color})
    );
    border-color: var(
      ${IconButtonVariableNames.HoverBorderColor},
      var(${IconButtonVariableNames.BorderColor})
    );
  }

  &:focus-visible {
    outline: 4px solid var(${IconButtonVariableNames.FocusOutlineColor});
  }

  &:disabled {
    background-color: var(${IconButtonVariableNames.DisabledBackgroundColor});
    cursor: not-allowed;
    color: var(${IconButtonVariableNames.DisabledColor});
  }

  &[aria-expanded='true'] {
    outline: 4px solid
      var(
        ${IconButtonVariableNames.ExpandedOutlineColor},
        var(${IconButtonVariableNames.FocusOutlineColor})
      );

    background-color: var(
      ${IconButtonVariableNames.ExpandedBackgroundColor},
      var(${IconButtonVariableNames.BackgroundColor})
    );
  }
`;
